// PreviewTester/components/CustomMessageModal.js
import React, { useState, useRef, useEffect } from 'react';

const CustomMessageModal = ({
  onClose,
  onAdd,
  availableVariables = [],
  position,
  positionSuggestions = [],
  onPositionChange,
  title = "✏️ Add Custom Message",
  placeholder = "Type your custom message... (use @ for variables)",
  buttonText = "Add Custom Message",
  isButton = false, // NEW: flag to differentiate
  hasForbiddenOptions = false, // NEW: show warning banner when some positions are disabled
}) => {
  const [message, setMessage] = useState('');
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [showVariablePicker, setShowVariablePicker] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedPosition, setSelectedPosition] = useState(position || 0);
  const inputRef = useRef(null);

  // Update local state when prop changes
  useEffect(() => {
    setSelectedPosition(position || 0);
  }, [position]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // ============================================================
  // ✅ Compute a safe selectedPosition: never let it point at a
  //    disabled (forbidden) option. If the incoming `position`
  //    happens to be disabled, snap to the nearest enabled one.
  // ============================================================
  useEffect(() => {
    if (!positionSuggestions || positionSuggestions.length === 0) return;

    const current = positionSuggestions.find(
      (s) => s.value === selectedPosition
    );

    // If current selection is missing or disabled, snap to a valid one
    if (!current || current.disabled) {
      const validSuggestion =
        positionSuggestions.find((s) => !s.disabled) ||
        positionSuggestions[0];
      if (validSuggestion && validSuggestion.value !== selectedPosition) {
        setSelectedPosition(validSuggestion.value);
        if (onPositionChange) {
          onPositionChange(validSuggestion.value);
        }
      }
    }
  }, [positionSuggestions, selectedPosition, onPositionChange]);

  const handleKeyDown = (e) => {
    if (e.key === '@') {
      const pos = inputRef.current?.selectionStart || 0;
      setCursorPosition(pos);
      setShowVariablePicker(true);
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleVariableSelect = (variable) => {
    const before = message.substring(0, cursorPosition);
    const after = message.substring(cursorPosition);
    const variableWithSpaces = `{{ ${variable} }}`;
    const newMessage = before + variableWithSpaces + after;
    setMessage(newMessage);
    setSelectedVariables([...selectedVariables, variable]);
    setShowVariablePicker(false);
    setTimeout(() => {
      if (inputRef.current) {
        const newPos = cursorPosition + variableWithSpaces.length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 10);
  };

  // ============================================================
  // ✅ Submit-time guard: if selectedPosition is disabled for any
  //    reason, redirect to the nearest valid suggestion.
  // ============================================================
  const handleAdd = () => {
    if (!message.trim()) return;

    let finalPosition = selectedPosition;
    const current = positionSuggestions.find(
      (s) => s.value === selectedPosition
    );

    if (current && current.disabled) {
      console.warn(
        '[CustomMessageModal] 🚫 Selected position is disabled — redirecting to safe fallback.'
      );
      const fallback = positionSuggestions.find((s) => !s.disabled);
      if (fallback) {
        finalPosition = fallback.value;
      }
    }

    onAdd(message, selectedVariables, finalPosition);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', color: '#2d3748' }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#718096',
            }}
          >
            ✕
          </button>
        </div>

        <p style={{ fontSize: '13px', color: '#718096', marginBottom: '12px' }}>
          {isButton
            ? 'Enter the button text. Use @ to insert variables from the data.'
            : 'Type your custom message. Use @ to insert variables from the data.'}
        </p>

        {/* ============================================================
            Warning banner — shown when some positions are disabled
            (Case 1 / Case 3 outer loop endfor)
            ============================================================ */}
        {hasForbiddenOptions && (
          <div style={{
            fontSize: '11px',
            color: '#c05621',
            backgroundColor: '#fffaf0',
            border: '1px solid #feebc8',
            borderRadius: '4px',
            padding: '6px 10px',
            marginBottom: '12px',
            lineHeight: '1.5',
          }}>
            ⚠️ Some positions are disabled — content placed outside the outermost loop would not appear inside any screen.
          </div>
        )}

        {/* Position selector */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '500',
            color: '#4a5568',
            marginBottom: '4px',
          }}>
            Insert after:
          </label>
          <select
            value={selectedPosition}
            onChange={(e) => {
              const newPosition = parseInt(e.target.value);
              setSelectedPosition(newPosition);
              if (onPositionChange) {
                onPositionChange(newPosition);
              }
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '13px',
              backgroundColor: 'white',
            }}
          >
            {positionSuggestions.map((suggestion) => (
              <option
                key={suggestion.value}
                value={suggestion.value}
                disabled={suggestion.disabled}
                style={suggestion.disabled ? { color: '#a0aec0' } : undefined}
                title={
                  suggestion.disabled
                    ? 'Cannot place content after the outer loop — it would be outside the screen(s).'
                    : undefined
                }
              >
                {suggestion.label}
              </option>
            ))}
          </select>
        </div>

        {/* Message input */}
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '500',
            color: '#4a5568',
            marginBottom: '4px',
          }}>
            {isButton ? 'Button Text:' : 'Message:'}
          </label>
          <input
            ref={inputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '2px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => e.target.style.borderColor = isButton ? '#48bb78' : '#4299e1'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />

          {/* Variable picker dropdown */}
          {showVariablePicker && availableVariables.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1001,
              maxHeight: '200px',
              overflowY: 'auto',
            }}>
              {availableVariables.map((v) => (
                <div
                  key={v}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    borderBottom: '1px solid #f7fafc',
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f4ff'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  onClick={() => handleVariableSelect(v)}
                >
                  {v}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected variables */}
        {selectedVariables.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', color: '#718096' }}>Variables used:</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
              {selectedVariables.map((v) => (
                <span key={v} style={{
                  padding: '2px 8px',
                  backgroundColor: '#ebf8ff',
                  color: '#2b6cb0',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontFamily: 'monospace',
                }}>
                  {v}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Preview */}
        {message && (
          <div style={{
            marginBottom: '12px',
            padding: '8px 12px',
            backgroundColor: isButton ? '#f0fff4' : '#f7fafc',
            borderRadius: '4px',
            border: `1px solid ${isButton ? '#c6f6d5' : '#e2e8f0'}`,
          }}>
            <span style={{ fontSize: '11px', color: '#718096' }}>
              {isButton ? 'Button Preview:' : 'Preview:'}
            </span>
            <div style={{
              fontSize: '13px',
              fontFamily: 'monospace',
              marginTop: '4px',
              color: isButton ? '#22543d' : '#2d3748'
            }}>
              {message}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#4a5568',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!message.trim()}
            style={{
              padding: '8px 16px',
              backgroundColor: message.trim() ? (isButton ? '#48bb78' : '#9f7aea') : '#e2e8f0',
              color: message.trim() ? 'white' : '#a0aec0',
              border: 'none',
              borderRadius: '4px',
              cursor: message.trim() ? 'pointer' : 'not-allowed',
              fontSize: '13px',
              fontWeight: '500',
            }}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomMessageModal;